import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { resolveFieldLabel, resolveLabel } from '../utils/fieldLabels';
import Select from './Select';

const HTML_TYPE = {
  text: 'text',
  number: 'number',
  date: 'date',
  email: 'email',
  phone: 'tel',
  url: 'url',
};

// Mantenere allineato alla whitelist server-side (AttachmentValidator::ALLOWED_MIME_TYPES,
// backend) - qui serve solo a pre-filtrare la finestra di selezione file, la
// validazione che conta è quella lato server sullo sniffing del contenuto.
const ATTACHMENT_ACCEPT = '.jpg,.jpeg,.png,.webp,.gif,.pdf,.txt,.zip,.doc,.docx,.xls,.xlsx';
// Tenere allineato a PublicTicketController::DEFAULT_MAX_ATTACHMENTS - usato
// solo per campi creati prima dell'opzione max_files (options.max_files nullo).
const DEFAULT_MAX_ATTACHMENTS = 5;

/**
 * Renderizza un campo del form dinamico di un progetto (form builder),
 * in base al suo field_type. Usato sia nel form pubblico/staff (editabile)
 * sia nel dettaglio ticket (readOnly).
 */
function DynamicFieldInput({ field, value, onChange, locale, fallbackLocale, readOnly = false, error = null }) {
  const { t } = useLanguage();
  const label = resolveFieldLabel(field, locale, fallbackLocale);
  const groupClassName = `form-group${error ? ' has-error' : ''}`;
  const errorMessage = error ? <p className="field-error-message">{error}</p> : null;

  if (readOnly) {
    let display = value;
    if (field.field_type === 'checkbox') {
      display = value === '1' ? '✓' : '—';
    } else if (field.field_type === 'select' && Array.isArray(field.options)) {
      const opt = field.options.find((o) => o.value === value);
      display = opt ? resolveLabel(opt.labels, locale, fallbackLocale) : value;
    }
    return (
      <div className="form-group">
        <label>{label}</label>
        <div>{display || '—'}</div>
      </div>
    );
  }

  if (field.field_type === 'checkbox') {
    return (
      <div className={groupClassName}>
        <label>
          <input
            type="checkbox"
            checked={value === '1' || value === true}
            onChange={(e) => onChange(e.target.checked ? '1' : '0')}
            required={field.is_required}
          />{' '}
          {label}
        </label>
        {errorMessage}
      </div>
    );
  }

  if (field.field_type === 'attachment') {
    const maxFiles = Number(field.options?.max_files) || DEFAULT_MAX_ATTACHMENTS;
    const files = Array.isArray(value) ? value : [];
    const addFiles = (picked) => {
      const merged = [...files, ...Array.from(picked || [])].slice(0, maxFiles);
      onChange(merged);
    };
    const removeFile = (index) => onChange(files.filter((_, i) => i !== index));

    return (
      <div className={groupClassName} style={{ gridColumn: '1 / -1' }}>
        <label>{label}{field.is_required ? ' *' : ''}</label>
        <input
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          disabled={files.length >= maxFiles}
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />
        {files.length > 0 && (
          <ul className="attachment-file-list">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`}>
                <span>{f.name}</span>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeFile(i)}>
                  {t('tickets.removeAttachment')}
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="page-subtitle" style={{ margin: 0 }}>{t('tickets.fieldAttachmentHint', { max: maxFiles })}</p>
        {errorMessage}
      </div>
    );
  }

  if (field.field_type === 'textarea') {
    return (
      <div className={groupClassName} style={{ gridColumn: '1 / -1' }}>
        <label>{label}{field.is_required ? ' *' : ''}</label>
        <textarea
          rows={3}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          required={field.is_required}
        />
        {errorMessage}
      </div>
    );
  }

  if (field.field_type === 'select') {
    return (
      <div className={groupClassName}>
        <label>{label}{field.is_required ? ' *' : ''}</label>
        <Select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          required={field.is_required}
        >
          <option value="">—</option>
          {(field.options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {resolveLabel(opt.labels, locale, fallbackLocale) || opt.value}
            </option>
          ))}
        </Select>
        {errorMessage}
      </div>
    );
  }

  return (
    <div className={groupClassName}>
      <label>{label}{field.is_required ? ' *' : ''}</label>
      <input
        type={HTML_TYPE[field.field_type] || 'text'}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        required={field.is_required}
      />
      {errorMessage}
    </div>
  );
}

export default DynamicFieldInput;
